/**
 * English version of the help guides (src/help/guides.js), by guide id. Only the texts are
 * translated: routes, permissions and modules stay in guides.js. Button names follow en.json.
 */

export const HELP_SECTIONS_EN = {
    start: 'Getting started',
    catalog: 'Catalog',
    shops: 'Stores',
    stores: 'Warehouses',
    sales: 'Checkout and sales',
    ecommerce: 'Online sales',
    marketing: 'Customers and marketing',
    purchases: 'Suppliers',
    reports: 'Statistics and reports',
    settings: 'Settings',
}

export const HELP_GUIDES_EN = {
    // --- Getting started ---
    'first-steps': {
        title: 'Your first steps with Qomerxia',
        steps: [
            'Create your items in <strong>Catalog</strong> (name, price, photo, category).',
            'Add them to your store: <strong>Stores</strong> › your store › <strong>Items</strong> › <strong>Add items</strong>.',
            'Create a cash register in the store (<strong>Cash register</strong> › <strong>Create cash register</strong>).',
            'Open <strong>Sales interface</strong> at the top of the screen to ring up your first sales.',
            'Invite your team in <strong>Settings</strong> › <strong>Users</strong>.',
        ],
        tip: 'The “Help” button is available on every page: it first suggests the guides for the page you are on.',
    },
    'switch-shop': {
        title: 'Change the active store',
        steps: [
            'At the top right, click the name of the active store.',
            'Pick the store in the list.',
            'The cash register, sales and stock shown are now those of this store.',
        ],
    },

    // --- Catalog ---
    'create-article': {
        title: 'Create an item',
        steps: [
            'Open <strong>Catalog</strong> › <strong>Items</strong>.',
            'Click <strong>New item</strong>.',
            'Enter the name, category and selling price (and the purchase price to track your margins).',
            'Add a photo with <strong>Add images to the item</strong>.',
            'To put it on sale right away, use <strong>Add to a shop</strong>, then <strong>Save</strong>.',
        ],
        tip: 'No barcode? Use “Automatically generate a barcode”.',
    },
    'article-variations': {
        title: 'Sell an item in several sizes or colors',
        steps: [
            'First create the attribute and its values: <strong>Catalog</strong> › <strong>Attributes/Values</strong> › <strong>New attribute</strong> (e.g. Size: S, M, L).',
            'Open the item, then click <strong>Create variations</strong>.',
            'Pick the values: one variation is created for each combination.',
            'Adjust the price and barcode of each variation if needed, then <strong>Save</strong>.',
        ],
    },
    'import-articles': {
        title: 'Import or export items',
        steps: [
            'Open <strong>Catalog</strong> › <strong>Items</strong>, then <strong>Import from Excel</strong>.',
            'Click <strong>Download the template</strong> and fill in the “Articles” sheet (one sub-category per item; a new one is created if you give its main category).',
            'Drop the filled file, then <strong>See the preview</strong>: nothing is saved yet, check new items, updates and errors.',
            'Click <strong>Import … item(s)</strong>. Lines with errors are skipped: fix them in the file and send it again.',
            'To get your catalog out, use <strong>Export</strong>; for photos in bulk, <strong>Import photos</strong>.',
        ],
    },
    categories: {
        title: 'Organize items into categories',
        steps: [
            'Open <strong>Catalog</strong> › <strong>Categories</strong>.',
            'Click <strong>New category</strong>, or <strong>New parent category</strong> to group several categories.',
            'Give it a name, and an image if the category is shown on the online store.',
            'Save: the category can then be picked on each item.',
        ],
    },
    bundles: {
        title: 'Create a bundle (meal deal, promotional set)',
        steps: [
            'Open <strong>Catalog</strong> › <strong>Bundles</strong>, then <strong>New bundle</strong>.',
            'Give the bundle a title.',
            'Add at least two items with <strong>Add an item</strong>, with their quantities.',
            'Click <strong>Save</strong>. “Cancel” goes back to the list without saving anything.',
        ],
        tip: 'The bundle cannot be saved if one of its items does not have enough stock.',
    },
    'brands-taxes': {
        title: 'Brands, taxes and batches',
        steps: [
            '<strong>Brands</strong>: <strong>New brand</strong> to filter and show the brand of your items.',
            '<strong>Taxes</strong>: <strong>New tax</strong> (e.g. VAT 18%), then pick it on the item.',
            '<strong>Item batches</strong>: <strong>New batch</strong> to track batch numbers and expiry dates.',
        ],
    },
    'product-options': {
        title: 'Offer options (sides, add-ons)',
        steps: [
            'Open <strong>Catalog</strong> › <strong>Options & add-ons</strong>, then <strong>New option group</strong>.',
            'Name the group (e.g. “Side”) and set how many choices the customer can make.',
            'Add the options with <strong>Add an option</strong>, with a price if they are paid.',
            'Pick the items concerned, then <strong>Save</strong>.',
        ],
    },

    // --- Stores ---
    'create-shop': {
        title: 'Create a store',
        steps: [
            'Open <strong>Stores</strong>, then <strong>New store</strong>.',
            'Enter the name, address and phone number.',
            'Save, then add the store’s items and collaborators.',
        ],
        tip: 'The number of stores depends on your plan.',
    },
    'shop-add-articles': {
        title: 'Put items on sale in a store',
        steps: [
            'On the store page, open <strong>Items</strong>.',
            'Click <strong>Add items</strong>, or <strong>Add items by categories</strong> to go faster.',
            'Check the selling price in the store, then save.',
            'Out-of-stock and slow-moving items have their own tabs.',
        ],
    },
    'shop-entries': {
        title: 'Receive stock in the store',
        steps: [
            'On the store page, open <strong>Stock</strong> › <strong>Stock entries</strong>.',
            'Goods sent by a warehouse, another store or a supplier arrive here.',
            'Open the entry, check the quantities received, then <strong>Validate</strong>.',
            'The store’s stock only goes up after validation.',
        ],
    },
    'shop-transfer': {
        title: 'Transfer stock to another store',
        steps: [
            'On the store page, open <strong>Stock</strong> › <strong>Stock transfer</strong>.',
            'Create an output order and pick the receiving store.',
            'Add the items and quantities, then validate.',
            'The receiving store validates the entry in its <strong>Stock entries</strong>.',
        ],
    },
    'shop-inventory': {
        title: 'Take a store inventory',
        steps: [
            'On the store page, open <strong>Stock</strong> › <strong>Inventories</strong>, then <strong>New inventory</strong>.',
            'Choose all products or only some categories, then <strong>Start inventory</strong>.',
            'Count: scan the barcode (the line opens) or type the quantity on each line.',
            'The “With difference” filter shows the products whose count differs from the stock.',
            'Click <strong>Validate inventory</strong>: the stock is corrected by the difference found.',
        ],
        tip: 'The store can keep selling while you count: sales made after a product was counted are taken into account.',
    },
    'shop-users': {
        title: 'Add collaborators to a store',
        steps: [
            'On the store page, open <strong>Collaborators</strong>.',
            'Click <strong>Add collaborators</strong> and pick the users.',
            'Give them a store role if their rights must be different here.',
        ],
        tip: 'Users are created first in Settings › Users.',
    },
    'shop-open-close': {
        title: 'Close or reopen the online store',
        steps: [
            'Open the store page.',
            'Click <strong>Close shop</strong>: customers can no longer order online.',
            'Click <strong>Open shop</strong> to take orders again.',
        ],
    },

    // --- Warehouses ---
    'create-store': {
        title: 'Create a warehouse',
        steps: [
            'Open <strong>Warehouses</strong>, then <strong>New warehouse</strong>.',
            'Enter the name, address and phone number, then save.',
            'Add the warehouse’s items and storekeepers from its page.',
        ],
        tip: 'A warehouse is a storage place (depot, stockroom): it does not sell, it supplies your stores.',
    },
    'store-entry': {
        title: 'Bring goods into the warehouse',
        steps: [
            'On the warehouse page, open <strong>Stock</strong> › <strong>Stock movement</strong>.',
            'Create an entry order (<strong>Create entry order</strong>).',
            'Add the items received and their quantities, then save.',
        ],
    },
    'store-to-shop': {
        title: 'Supply a store from the warehouse',
        steps: [
            'On the warehouse page, open <strong>Stock</strong> › <strong>Stock movement</strong>.',
            'Create an output order (<strong>Create output order</strong>) to the store.',
            'Add the items and quantities, then validate the output.',
            'The store confirms receipt in its <strong>Stock entries</strong>.',
        ],
    },
    'store-unvalidated': {
        title: 'Handle unvalidated entries',
        steps: [
            'On the warehouse page, open <strong>Stock</strong> › <strong>Unvalidated entries</strong>.',
            'Open the entry and check the quantities actually received.',
            'Validate: the warehouse stock is updated.',
        ],
    },
    'store-inventory': {
        title: 'Take a warehouse inventory',
        steps: [
            'On the warehouse page, open <strong>Stock</strong> › <strong>Inventories</strong>, then <strong>New inventory</strong>.',
            'Choose all products or some categories, then <strong>Start inventory</strong>.',
            'Count by scanning barcodes or typing the quantities.',
            'Click <strong>Validate inventory</strong>: the warehouse stock is corrected by the difference found.',
        ],
        tip: 'Only one inventory in progress per warehouse; it is separate from the stores’ inventories.',
    },
    'store-users': {
        title: 'Add a storekeeper',
        steps: [
            'On the warehouse page, open <strong>Collaborators</strong>.',
            'Click <strong>Add a storekeeper</strong> and pick the user.',
        ],
    },

    // --- Checkout and sales ---
    'pos-sell': {
        title: 'Ring up a sale',
        steps: [
            'Click <strong>Sales interface</strong> at the top of the screen.',
            'If no register is open, click <strong>Open a session</strong> and enter the opening cash fund.',
            'Add items by tapping them or scanning their barcode.',
            'To put the sale in a customer\'s name, tap <strong>Customer</strong>: search by name or phone and tap them. Not there? <strong>New customer</strong>, enter their full name (and phone), then <strong>Save and select</strong>.',
            'Pick the payment method (cash, Mobile Money…), then validate. Customer paying later or leaving a deposit? See “Sell on credit or with a deposit”.',
            'Print the receipt if the customer asks for it.',
        ],
    },
    'pos-close': {
        title: 'Close the register at the end of the day',
        steps: [
            'In the sales interface, click <strong>Close register</strong>.',
            'Count the cash and enter the closing cash fund.',
            'Confirm: the session summary (sales, payments) remains available.',
            'Find all sessions on the store page › <strong>Sales</strong> › <strong>Cash register sessions</strong>.',
        ],
    },
    'shop-boxes': {
        title: 'Create a cash register',
        steps: [
            'On the store page, open <strong>Cash register</strong>.',
            'Click <strong>Create cash register</strong> and give it a name (e.g. “Register 1”).',
            'Each register has its own sessions: useful when several sellers ring up sales at the same time.',
        ],
    },
    'sales-history': {
        title: 'Find a sale and reprint the receipt',
        steps: [
            'On the store page, open <strong>Sales</strong>.',
            'Filter by date or search for the sale number.',
            'Open the sale to see the items and payment, then <strong>Print invoice</strong>.',
        ],
    },

    // --- Online sales ---
    'pos-credit': {
        title: 'Sell on credit or with a deposit',
        steps: [
            'In the register, add the items then tap <strong>Payment</strong>.',
            'Pick the customer with <strong>Customer</strong>: a credit sale is always in a customer\'s name.',
            'Turn on <strong>Credit sale / deposit</strong>. Set a <strong>Due date</strong> if needed (optional).',
            'Customer leaving a deposit? Pick the payment method and enter the amount on the keypad. Otherwise, validate directly.',
            'The balance shows in the store page › <strong>Sales</strong> › <strong>Customer credits</strong>.',
        ],
    },
    'credits-collect': {
        title: 'Collect a payment from a customer who owes money',
        steps: [
            'In the store page, open <strong>Sales</strong> › <strong>Customer credits</strong>: the total due, the customers concerned and overdue amounts are shown at the top.',
            'Find the customer (name, phone or sale number), or tick <strong>Overdue only</strong>.',
            'Expand the customer and click <strong>Collect</strong> next to the sale.',
            'Enter the <strong>Amount received</strong> (or <strong>Whole balance</strong>) and the payment method, then validate. The sale\'s <strong>Payment history</strong> is in the same window.',
            'If you have a register open in this store, the payment is counted in it.',
        ],
    },
    'pos-order-forms': {
        title: 'Save a purchase order and have it paid later',
        steps: [
            'In the register, prepare the cart (items, customer, note), then tap <strong>Receipt</strong>.',
            'In the preview, tap <strong>Save as purchase order</strong>: it gets a number (BC-00001…) and can be printed with <strong>Print the order</strong>. Stock only moves when it is paid.',
            'To find it again, open <strong>Purchase orders</strong> at the top of the register: every pending order of the store is there, whatever the cashier.',
            'Tap <strong>Open</strong>, then <strong>Pay</strong> to ring it up like a normal sale, or <strong>Edit</strong> to change the cart (then <strong>Update order</strong> from Receipt).',
            'An order that will not be paid is removed with <strong>Cancel</strong> (reason optional).',
        ],
    },
    'ecommerce-orders': {
        title: 'Process an online order',
        steps: [
            'Open <strong>E-commerce</strong> › <strong>Orders</strong>: new orders are flagged with a red dot.',
            'Open the order and check the items and the address.',
            'Update its status as you go (preparation, delivery…).',
            'Marking it <strong>Delivered</strong> requires the customer\'s 6-digit code: see “Hand over an order with the customer\'s code”.',
        ],
    },
    'ecommerce-deposit': {
        title: 'Ask for a deposit to confirm an order',
        steps: [
            'Open <strong>Settings</strong> › <strong>System</strong> › <strong>Payment methods</strong> and click the pencil of <strong>Cash on delivery</strong>.',
            'Choose <strong>Percentage</strong> (e.g. 30% of the total) or <strong>Fixed amount</strong>, then what it applies to: delivery and pickup, home delivery only or pickup only.',
            'Optional: a minimum order total, so small orders need no deposit. Then <strong>Save</strong>.',
            'At checkout, the customer pays the deposit by Mobile Money; the order is only created once it is paid. The balance is collected on delivery or at pickup.',
            'In <strong>E-commerce</strong> › <strong>Orders</strong>, a <strong>Deposit</strong> badge flags these orders; the detail shows the deposit paid and the balance to collect.',
            'Canceled order: refund the deposit to the customer yourself (the payment reference is shown in the order). The financial report, <strong>Deposits</strong> view, lists those to refund.',
        ],
        tip: 'Mobile Money must be enabled and configured: without it (or during the free trial), no deposit is asked and orders go through as before.',
    },
    'ecommerce-deliveries': {
        title: 'Track deliveries',
        steps: [
            'Open <strong>E-commerce</strong> › <strong>Deliveries</strong>.',
            'Follow the status of each delivery and the driver in charge.',
            'Each driver finds their trips in <strong>My deliveries</strong>, with the <strong>Amount to collect</strong> when the customer pays on delivery (deposit already paid deducted).',
            'At handover, the driver taps <strong>Confirm delivery</strong> and enters the customer\'s code.',
            'Need another driver on the way? Open the order and use <strong>Change delivery person</strong>.',
            'External drivers are declared in <strong>Settings</strong> › <strong>External drivers</strong>.',
        ],
    },
    'handover-code': {
        title: 'Hand over an order with the customer\'s code',
        steps: [
            'With each order, the customer receives a 6-digit code (WhatsApp, e-mail) and a link to track the order without an account.',
            'When handing over the order, tap <strong>Confirm delivery</strong> (or <strong>Confirm pickup</strong>) and ask the customer for the code.',
            'Enter it, tap <strong>Check</strong>, then <strong>Confirm handover</strong>. For a pickup, note the person\'s name in <strong>Collected by</strong> if needed.',
            'Customer lost the code? <strong>Resend the code to the customer</strong> sends it again by WhatsApp.',
            'As a last resort, a manager can <strong>Confirm without code</strong> with a reason, saved on the order.',
        ],
    },
    'delivery-areas': {
        title: 'Set delivery rates',
        steps: [
            'Open <strong>Settings</strong> › <strong>eCommerce settings</strong> › <strong>Delivery rates</strong>.',
            'Click <strong>New zone</strong>: a name and, if you know it, the price (can be changed later).',
            'Open the zone, then <strong>Constitute zone</strong> to add the cities and neighborhoods covered.',
            'Change the price with <strong>Edit</strong>, on the zone or in the list.',
        ],
        tip: 'A neighborhood can belong to only one zone per store.',
    },
    'ecommerce-reviews': {
        title: 'Moderate customer reviews',
        steps: [
            'Open <strong>E-commerce</strong> › <strong>Customer reviews</strong>.',
            'Unpublished reviews are waiting for your approval.',
            'Publish the reviews you want to show on the online store.',
        ],
    },

    // --- Customers and marketing ---
    customers: {
        title: 'Add or import customers',
        steps: [
            'Open <strong>Customers</strong>, then <strong>New customer</strong>.',
            'Enter the name (first name is optional) and phone number, and an address with <strong>New address</strong> for deliveries.',
            'At the register, you can also create a customer without leaving the sale: <strong>Customer</strong> › <strong>New customer</strong>.',
            'For an existing list, use <strong>Import / Export</strong>.',
        ],
    },
    promotion: {
        title: 'Run a promotion',
        steps: [
            'Open <strong>Promotions</strong>, then <strong>New promotion</strong>.',
            'Choose the type (discount in % or as an amount, “Buy X get Y”…) and the dates.',
            'Add the products (<strong>Add products</strong> or <strong>Add by category</strong>), or tick <strong>Apply to all products</strong>.',
            'Save: the promotion applies at the register and online during its dates.',
        ],
    },
    'promo-code': {
        title: 'Create a promo code',
        steps: [
            'Open <strong>Promo code</strong>, then <strong>New promo code</strong>.',
            'Choose the code, the discount and the validity period.',
            'Share the code with your customers: they enter it when paying online.',
        ],
    },
    loyalty: {
        title: 'Set up loyalty',
        steps: [
            'Open <strong>Loyalty</strong>, then <strong>New loyalty program</strong>.',
            'Choose how customers earn points: based on the amount spent (e.g. 1 point per 1,000 FCFA) or for each purchase.',
            'Add rewards with <strong>Add a tier</strong>, then <strong>Save tiers</strong>.',
            'Points are added automatically when the customer is identified at the register or online.',
        ],
    },

    // --- Suppliers ---
    'supplier-purchase': {
        title: 'Order from a supplier and receive the goods',
        steps: [
            'Open <strong>Supplier purchase orders</strong>, then <strong>New purchase order</strong>.',
            'Pick the supplier, add the items, quantities and purchase prices.',
            'On receipt, click <strong>Validate/Transfer</strong> and choose where the goods go (store or warehouse).',
            'The stock goes up and the items’ purchase price is updated.',
        ],
    },

    // --- Statistics and reports ---
    reports: {
        title: 'Produce a report (sales, products, finance)',
        steps: [
            'Open <strong>Reports</strong> and pick the report.',
            'Choose the period and the store.',
            'Tick the views to display: the export contains exactly what is on screen.',
            'Click Export.',
        ],
    },
    statistics: {
        title: 'Read the statistics',
        steps: [
            'The <strong>Dashboard</strong> sums up today’s and this month’s activity.',
            '<strong>Statistics</strong> details sales, customers, products and profitability.',
            'Choose the period you want on each page.',
        ],
        tip: 'Profitability uses the items’ purchase price: fill it in the catalog.',
    },

    // --- Settings ---
    'users-roles': {
        title: 'Create a user and choose their rights',
        steps: [
            'Open <strong>Settings</strong> › <strong>Users</strong>, then <strong>New user</strong>.',
            'Enter their name and email or phone, and choose their role (e.g. Gérant for a manager, Caissier for a cashier).',
            'To adjust what a role allows, open <strong>Settings</strong> › <strong>Roles</strong> and tick the rights.',
            'Then attach the user to their stores (store page › <strong>Collaborators</strong>).',
        ],
        tip: 'The number of users depends on your plan.',
    },
    'payment-methods': {
        title: 'Set up payment methods',
        steps: [
            'Open <strong>Settings</strong> › <strong>System</strong> › <strong>Payment methods</strong>.',
            'Turn each method offered to customers on or off with its switch (cash on delivery, Mobile Money, card).',
            'To get paid online, click the pencil of <strong>Mobile Money</strong> (KKiaPay or AlphaPay) or <strong>Card</strong> (Stripe) and enter your keys: the money goes straight to your account.',
            'These settings apply to every shop of the company.',
            'To ask for a deposit on cash-on-delivery orders, see “Ask for a deposit to confirm an order”.',
        ],
    },
    subscription: {
        title: 'View or renew my subscription',
        steps: [
            'Open <strong>Settings</strong> › <strong>My subscription</strong>.',
            'Your current plan and its end date are shown at the top.',
            'Pick a plan and click <strong>Choose and pay</strong>.',
            'An early renewal is added to the end of the current period: you don’t lose any days.',
        ],
        tip: 'Only the store owner can pay for the subscription.',
    },
    'company-info': {
        title: 'Fill in the company information',
        steps: [
            'Open <strong>Settings</strong> › <strong>System</strong> › <strong>Company configuration</strong>.',
            'Enter the name, logo, address and legal information: they appear on invoices.',
            'For normalized invoices, open <strong>Normalized invoices</strong> and add your configuration.',
        ],
    },
    support: {
        title: 'Contact support',
        steps: [
            'Click the headset icon at the top of the screen.',
            'Click <strong>New ticket</strong> and describe your problem (a screenshot helps a lot).',
            'You are notified as soon as the team replies.',
        ],
    },
}
